import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { EntityType, NarrativeType, Relationship } from '@/lib/types';
import { useEffect, useMemo, useState } from 'react';

export function useEntities<T extends { id: string }>(
  entityType: EntityType,
  enabled = true,
) {
  const { backend } = useFirebase();
  const { novel } = useNovel();

  const [data, setData] = useState<T[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!novel?.id && enabled);
  const [error, setError] = useState<Error | null>(null);

  const layoutMode = novel?.storageLayout?.entities?.[entityType]?.items || 'embedded';

  useEffect(() => {
    if (!novel?.id || !enabled) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const unsubscribe = backend.entities.subscribeEntities<T>(
      novel.ownerId,
      novel.id,
      novel.storageLayout,
      entityType,
      (items) => {
        setData(items);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [backend, novel?.ownerId, novel?.id, layoutMode, entityType, enabled]);

  const effectiveIsLoading = enabled && !!novel?.id ? isLoading || (data === null && !error) : false;

  return useMemo(
    () => ({ data, isLoading: effectiveIsLoading, error }),
    [data, effectiveIsLoading, error],
  );
}

export function useAllRelationships() {
  const { backend } = useFirebase();
  const { novel } = useNovel();

  const [data, setData] = useState<Relationship[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!novel?.id);
  const [error, setError] = useState<Error | null>(null);

  const layoutMode = novel?.storageLayout?.entities?.relationships?.items || 'embedded';

  useEffect(() => {
    if (!novel?.id) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const unsubscribe = backend.entities.subscribeAllRelationships(
      novel.ownerId,
      novel.id,
      novel.storageLayout,
      (items) => {
        setData(items);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [backend, novel?.ownerId, novel?.id, layoutMode]);

  return useMemo(() => ({ data, isLoading, error }), [data, isLoading, error]);
}

export function useNarrativeItems<T extends { id: string }>(
  narrativeType: NarrativeType
) {
  const { backend } = useFirebase();
  const { novel } = useNovel();

  const [data, setData] = useState<T[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!novel?.id);
  const [error, setError] = useState<Error | null>(null);

  const layoutMode = novel?.storageLayout?.narrative?.[narrativeType]?.items || 'embedded';

  useEffect(() => {
    if (!novel?.id) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const unsubscribe = backend.narrative.subscribeNarrativeItems<T>(
      novel.ownerId,
      novel.id,
      novel.storageLayout,
      narrativeType,
      (items) => {
        setData(items);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [backend, novel?.ownerId, novel?.id, layoutMode, narrativeType]);

  return useMemo(() => ({ data, isLoading, error }), [data, isLoading, error]);
}
